import React, { useEffect, useState } from "react"
import { Modal, Form, Select, Checkbox, Button, Spin, Popconfirm, message, Row, Col } from "antd"
import { CheckboxGroupProps } from "antd/es/checkbox"
import { useRegister } from "../../contexts/RegisterContext"
import { useCompany } from "../../contexts/CompanyContext"
import { useSecureRequest } from "../../hooks/useSecureRequest"
import { useStore } from "../../contexts/StoreContext"
import { useConfig } from "../../contexts/ConfigContext"
import { modules } from "../../config/pages"
import { mutate } from "swr"
import RegisterStorageItemForm from "../forms/RegisterStorageItemForm"
import RegisterServiceForm from "../forms/RegisterServiceForm"
import RegisterFixedAssetForm from "../forms/RegisterFixedAssetForm"
import ConversionModal from "./ConversionModal"
import { TStorageItemRS } from "../../models/storageModel"

import {
  getCheckboxOptions,
  checkboxOptions,
  getFormTaxes,
  getOtherValues,
  getPrices,
  getModuleElementOptions,
  toDataForm,
} from "./view-item/viewItemModalHelpers"
import ViewProductReadOnly from "./view-item/ViewProductReadOnly"

export interface ViewItemModalProps {
  visible: boolean
  item: TStorageItemRS | null
  onCancel: () => void
  onSuccess: () => void
  /** Si false → sólo lectura (sin botón Editar). Default: true */
  canEdit?: boolean
}

export const ViewItemModal: React.FC<ViewItemModalProps> = ({
  visible,
  item: initialItem,
  onCancel,
  onSuccess,
  canEdit = true,
}) => {
  const { register, updateRegister } = useRegister()
  const { company } = useCompany()
  const { store } = useStore()
  const { securePost } = useSecureRequest()
  const { config, updateConfig } = useConfig()

  const [form] = Form.useForm()
  const [isFormMode, setIsFormMode] = useState(false)
  const [isConversionOpen, setIsConversionOpen] = useState(false)
  const [spinning, setSpinning] = useState(false)
  const [checkedList, setCheckedList] = useState<string[]>([])
  const [item, setItem] = useState<TStorageItemRS | null>(null)

  const { Option } = Select
  const register_type = Form.useWatch("register_type", form)

  // Sincronizar config.module para habilitar lógica de edición en formularios anidados
  useEffect(() => {
    if (visible && isFormMode) {
      const prevModule = config.module
      updateConfig({ module: modules.EditStorageItem })
      return () => {
        updateConfig({ module: prevModule })
      }
    }
  }, [visible, isFormMode])

  // Efecto: sincronizar cuando se abre el modal
  useEffect(() => {
    const loadFullItem = async () => {
      if (!initialItem?.uid) return
      setSpinning(true)
      try {
        const res = await securePost("/inventory/item/get-item", {
          uidItem: initialItem.uid,
          company: company.current?.uid,
          module: modules.EditStorageItem,
        })
        setSpinning(false)
        if (res.status === 200) {
          const fullData = res.inventoryItem || res.data
          setItem(fullData)
          updateRegister({ currentStorageItem: fullData })
          const formData = toDataForm(fullData, register)
          form.setFieldsValue(formData)
          setCheckedList(getCheckboxOptions(fullData))
        } else {
          message.error(res.message || "Error al cargar el detalle del producto")
          onCancel()
        }
      } catch (err) {
        console.error(err)
        setSpinning(false)
        message.error("Error de red al cargar el producto")
        onCancel()
      }
    }

    if (visible && initialItem) {
      setItem(initialItem)
      updateRegister({ currentStorageItem: initialItem })
      const formData = toDataForm(initialItem, register)
      form.setFieldsValue(formData)
      setCheckedList(getCheckboxOptions(initialItem))
      setIsFormMode(false)
      loadFullItem()
    }
    if (!visible) {
      setIsFormMode(false)
    }
  }, [visible, initialItem?.uid])

  // Guardar
  const onFinishForm = async (values: any) => {
    if (!item) return
    const availablePresentation = register.displayOptions.find((el: any) => el.show === true)
    const availableCategory = register.categoryOptions.find((el: any) => el.show === true)
    const registerOptions = values.registerOptions || []
    const isSale = registerOptions.join("-").includes("SALE")
    const isPurchase = registerOptions.join("-").includes("PURCHASE") || values.register_type === "FIXED_ASSET"
    const sellTaxes = isSale
      ? getFormTaxes({ ...values }, "sell", register, item)
      : (item?.module_options?.filter((el: any) => el.sub_type === "SELL_TAX") || []).map((el: any) => ({ ...el, is_active: false }))
    const purchaseTaxes = isPurchase
      ? getFormTaxes({ ...values }, "purchase", register, item)
      : (item?.module_options?.filter((el: any) => el.sub_type === "PURCHASE_TAX") || []).map((el: any) => ({ ...el, is_active: false }))
    const otherValues = getOtherValues({ ...values }, item)
    const prices = getPrices({ ...values }, registerOptions, item)
    const moduleElementOptions = getModuleElementOptions({ ...values }, register, item)
    const module_options = [...moduleElementOptions, ...sellTaxes, ...purchaseTaxes]

    const isFixedAsset = values.register_type === "FIXED_ASSET"
    const primaryImage = (values.images || []).find((img: any) => img.is_primary) || (values.images || [])[0]
    const primaryUrl = primaryImage?.file_url || values.image || item.image || ""

    const storageElement: Partial<TStorageItemRS> = {
      id: item.id,
      company_id: company.current?.id,
      custom_id: values.custom_id,
      barcode: values.barcode,
      description: values.description,
      sell_discount: isSale ? (values.sell_discount ?? 0) : 0,
      purchase_discount: isPurchase ? (values.purchase_discount ?? 0) : 0,
      min_quantity: isFixedAsset ? 0 : (values.min_quantity ?? 0),
      is_traceable: values.register_type === "SERVICE" ? false : (isFixedAsset ? (values.is_traceable !== undefined ? Boolean(values.is_traceable) : true) : Boolean(values.is_traceable)),
      name: values.name,
      purchase_item: isFixedAsset ? true : isPurchase,
      sell_item: isFixedAsset ? false : isSale,
      sku: values.sku,
      location: values.location,
      is_active: true,
      image_mini: values.image_mini || primaryUrl,
      image: primaryUrl,
      images: values.images !== undefined ? values.images : (item.images || []),
      company_uid: company.current?.uid || "",
      display: values.display || availablePresentation?.key || "UND",
      sell_price: isSale ? (values.sell_price ?? 0) : (item.sell_price ?? 0),
      purchase_price: isPurchase ? (values.purchase_price ?? 0) : (item.purchase_price ?? 0),
      quantity: 0,
      register_type: values.register_type,
      measure_unit: values.measure_unit || "UN",
      category: values.category || availableCategory?.key || "VRS",
      parent_element_id: values.parent_element_id,
      conversion_factor: values.conversion_factor,
      module_options,
      prices,
      other_values: otherValues,
    }

    // SERVICE overrides (igual que RegisterStorageElement)
    if (values.register_type === "SERVICE") {
      storageElement.display = "N/A"
      storageElement.location = "N/A"
      storageElement.measure_unit = "UN"
      storageElement.sell_item = isSale
      storageElement.purchase_item = isPurchase
    }

    setSpinning(true)
    const itemResponse = await securePost("/inventory/item/update-item", {
      storageElement,
      company: company.current?.uid,
      uidCompany: company.current?.uid,
      uidItem: item.uid,
      module: "EDIT_STORAGE_ITEM",
    })
    setSpinning(false)

    if (itemResponse.status === 200) {
      mutate((key) => Array.isArray(key) && key[0] === "/inventory/item/get-items", undefined, { revalidate: true })
      updateRegister({ currentStorageItem: undefined })
      message.success("Producto actualizado correctamente")
      onSuccess()
    } else {
      message.error(itemResponse.message || "Error al actualizar el producto")
    }
  }

  const onChangeCheckbox: CheckboxGroupProps["onChange"] = (list) => {
    setCheckedList(list as string[])
  }

  const displayItem = item ?? initialItem

  const modalTitle = isFormMode
    ? `Editar · ${displayItem?.name || ""}`
    : displayItem?.name || "Detalle del producto"

  const formFooter = [
    <Button key="cancel" onClick={() => setIsFormMode(false)}>
      Volver
    </Button>,
    <Popconfirm
      key="confirm"
      title="¿Confirmar actualización?"
      onConfirm={() => form.submit()}
      okText="Sí"
      cancelText="No"
    >
      <Button type="primary" loading={spinning}>
        Actualizar
      </Button>
    </Popconfirm>,
  ]

  return (
    <Modal
      title={<div style={{ textAlign: "center" }}>{modalTitle}</div>}
      open={visible}
      onCancel={onCancel}
      footer={isFormMode ? formFooter : null}
      maskClosable={false}
      destroyOnHidden
      width={820}
    >
      <Spin spinning={spinning}>
        {/* ── Modo vista ── */}
        {!isFormMode && displayItem && (() => {
          const canFractionate = Boolean(
            Number(displayItem.conversion_factor || 0) > 1 ||
            Boolean(displayItem.parent_element_id) ||
            register?.registerList?.some((i: any) => String(i.parent_element_id) === String(displayItem.id))
          )
          return (
            <ViewProductReadOnly
              item={displayItem}
              onEdit={() => setIsFormMode(true)}
              onFractionate={canFractionate ? () => setIsConversionOpen(true) : undefined}
              canEdit={canEdit}
              register={register}
              store={store}
            />
          )
        })()}

        {/* ── Modo formulario (edición) ── */}
        {isFormMode && displayItem && (
          <Form
            form={form}
            layout="vertical"
            name="edit-storage-item-modal"
            scrollToFirstError={{ block: "end", focus: true }}
            onFinish={onFinishForm}
          >
            {/* Tipo + opciones de registro */}
            <Row gutter={[10, 10]}>
              <Col xs={24} sm={{ span: 10, offset: 2 }}>
                <Form.Item label="Tipo de registro" name="register_type">
                  <Select disabled>
                    {register.itemTypes.map((el: any) => (
                      <Option key={el.id} value={el.key}>
                        {el.string_value}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {(register_type === "PRODUCT" || register_type === "SERVICE") && (
                <Col xs={24} sm={{ span: 10 }}>
                  <Form.Item
                    label="Opciones de registro"
                    name="registerOptions"
                    rules={[{ required: true, message: "Por favor selecciona una opción" }]}
                  >
                    <Checkbox.Group
                      options={checkboxOptions}
                      value={checkedList}
                      onChange={onChangeCheckbox}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            {/* Subformulario según tipo */}
            {register_type === "PRODUCT" && (
              <RegisterStorageItemForm form={form} getFormTaxes={(v, t) => getFormTaxes(v, t, register, item)} />
            )}
            {register_type === "SERVICE" && (
              <RegisterServiceForm form={form} getFormTaxes={(v, t) => getFormTaxes(v, t, register, item)} />
            )}
            {register_type === "FIXED_ASSET" && (
              <RegisterFixedAssetForm form={form} getFormTaxes={(v, t) => getFormTaxes(v, t, register, item)} />
            )}
          </Form>
        )}
      </Spin>

      <ConversionModal
        open={isConversionOpen}
        initialOriginItem={item ?? displayItem}
        onClose={() => setIsConversionOpen(false)}
        onSuccess={onSuccess}
      />
    </Modal>
  )
}

export default ViewItemModal
